import type { ReactNode } from 'react';
import { cx } from '../cx';
import { devWarn } from '../dev';
import { LineKey, NARROW_ONLY, WIDE_ONLY, type ChartLayout } from './layout';
import { CHART_MINIMUM, TICK_TEXT, chartStroke, niceScale, percentOf, thinTick } from './scale';

export type CumulativeSeries = {
  id: string;
  label: ReactNode;
  /** One value per ad. */
  values: number[];
};

export type CumulativeCurveProps = {
  /** One to four groups. Each needs at least 20 values; thinner ones are left out. */
  series: CumulativeSeries[];
  /** The story group: ink and thicker. The rest are context. */
  highlightId?: string;
  /**
   * The worked example the chart is built around: a dashed line at one value with words that read
   * it ("At €4,500: 96% of junior ads and 31% of senior ads pay that or less"). Compute the shares
   * with `shareAtOrBelow`, never by eye.
   */
  reading?: { value: number; label: ReactNode };
  /** One line on how to read the chart, written under it. Required: few readers know this form. */
  howToRead: ReactNode;
  formatTick: (value: number) => string;
  formatShare: (share: number) => string;
  /** `auto` (default) switches to the narrow form in a narrow container: a shorter plot, group names in a key under it. */
  layout?: ChartLayout;
  /** One sentence with the worked example, for screen readers. */
  description: string;
  className?: string;
};

const PLOT_H: Record<ChartLayout, string> = { auto: 'h-44 @md:h-52', narrow: 'h-44', wide: 'h-52' };

/** The share (0 to 1) of values at or below `x`. */
export function shareAtOrBelow(values: number[], x: number): number {
  if (!values.length) return 0;
  return values.filter((v) => v <= x).length / values.length;
}

/** The empirical cumulative curve as polyline points in a 0 to 100 box (steps, no smoothing). */
function steps(values: number[], min: number, max: number): string {
  const s = [...values].sort((a, b) => a - b);
  const x = (v: number) => percentOf(v, min, max);
  const pts = [`0,100`, `${x(s[0])},100`];
  s.forEach((v, i) => {
    pts.push(`${x(v)},${100 - (i / s.length) * 100}`, `${x(v)},${100 - ((i + 1) / s.length) * 100}`);
  });
  pts.push(`100,0`);
  return pts.join(' ');
}

/** Where a curve reaches a share: the value at that quantile. */
const quantile = (values: number[], q: number) => {
  const s = [...values].sort((a, b) => a - b);
  return s[Math.min(s.length - 1, Math.floor(q * s.length))];
};

/**
 * The share of ads at or below each value, one step line per group: "what share of junior ads pays
 * under €3,500". No bins, so percentiles read straight off the curve. Always shown with a line on
 * how to read it and one worked example on the chart.
 */
export function CumulativeCurve({
  series: raw,
  highlightId,
  reading,
  howToRead,
  formatTick,
  formatShare,
  layout = 'auto',
  description,
  className,
}: CumulativeCurveProps) {
  devWarn(raw.length > 4, `CumulativeCurve has ${raw.length} groups; four at most.`);
  const series = raw.filter((s) => s.values.length >= CHART_MINIMUM.spread);
  devWarn(series.length < raw.length, `CumulativeCurve left out groups under ${CHART_MINIMUM.spread} values.`);
  const all = series.flatMap((s) => s.values);
  if (!all.length) return null;
  const scale = niceScale(Math.min(...all), Math.max(...all));
  const story = highlightId ?? (series.length === 1 ? series[0].id : undefined);
  const shareTicks = [0, 0.25, 0.5, 0.75, 1];
  return (
    <div className={cx('@container flex flex-col gap-2', className)}>
      <div role="img" aria-label={description} className="flex flex-col gap-1">
        <div className="flex gap-2">
          <div className={cx('relative w-10 shrink-0', PLOT_H[layout])} aria-hidden="true">
            {shareTicks.map((t) => (
              <span
                key={t}
                className={cx('absolute right-0 translate-y-1/2', TICK_TEXT)}
                style={{ bottom: `${t * 100}%` }}
              >
                {formatShare(t)}
              </span>
            ))}
          </div>
          <div className={cx('relative mr-4 flex-1', PLOT_H[layout])}>
            {shareTicks.map((t) => (
              <span
                key={t}
                className="absolute inset-x-0 border-t border-jc-chart-grid"
                style={{ bottom: `${t * 100}%` }}
              />
            ))}
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              className="absolute inset-0 size-full overflow-visible motion-safe:animate-jc-fade"
            >
              {[...series.filter((s) => s.id !== story), ...series.filter((s) => s.id === story)].map((s) => (
                <polyline
                  key={s.id}
                  points={steps(s.values, scale.min, scale.max)}
                  fill="none"
                  vectorEffect="non-scaling-stroke"
                  strokeWidth={s.id === story ? 2.5 : 1.75}
                  strokeLinejoin="round"
                  className={chartStroke(s.id === story ? 'subject' : 'context')}
                />
              ))}
            </svg>
            {series.map((s) => (
              <span
                key={s.id}
                aria-hidden="true"
                className={cx(
                  'absolute -translate-x-full -translate-y-1/2 pr-2 text-xs whitespace-nowrap',
                  WIDE_ONLY[layout],
                  s.id === story ? 'font-semibold text-jc-ink' : 'text-jc-ink-secondary',
                )}
                style={{ left: `${percentOf(quantile(s.values, 0.8), scale.min, scale.max)}%`, top: '20%' }}
              >
                {s.label}
              </span>
            ))}
            {reading ? (
              <span
                aria-hidden="true"
                className="absolute inset-y-0 border-l-2 border-dashed border-jc-ink-secondary"
                style={{ left: `${percentOf(reading.value, scale.min, scale.max)}%` }}
              />
            ) : null}
          </div>
        </div>
        <div className="relative mr-4 ml-12 h-4" aria-hidden="true">
          {scale.ticks.map((t, i) => (
            <span
              key={t}
              className={cx('absolute -translate-x-1/2 whitespace-nowrap', TICK_TEXT, thinTick(i, scale.ticks.length))}
              style={{ left: `${percentOf(t, scale.min, scale.max)}%` }}
            >
              {formatTick(t)}
            </span>
          ))}
        </div>
      </div>
      <LineKey
        className={NARROW_ONLY[layout]}
        items={series.map((s) => ({ id: s.id, label: s.label, story: s.id === story }))}
      />
      {/* The worked example sits under the chart, keyed to the dashed line, so it never covers a curve. */}
      {reading ? (
        <p className="flex items-start gap-2 type-meta font-semibold text-jc-ink">
          <span className="mt-0.5 h-4 shrink-0 border-l-2 border-dashed border-jc-ink-secondary" aria-hidden="true" />
          {reading.label}
        </p>
      ) : null}
      <p className="type-caption text-jc-ink-secondary">{howToRead}</p>
    </div>
  );
}
